/**
 * BottomSheet.tsx
 *
 * Shared header-opened bottom-sheet primitive (#1976 Phase 2). Owns the chrome
 * common to every context sheet: backdrop, Escape-to-close, body-scroll-lock,
 * grab handle, title + close header, reduced-motion-aware slide-up, adaptive
 * height (caps at 80vh; the content area scrolls). Presentational shell only —
 * callers supply the content (ClubList, RoomList, or both).
 *
 * Extracted from the Phase 1 ConversationSheet so club switching (ClubSwitcherModal,
 * retired) and conversation switching render through one consistent pattern.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { getInitialReducedMotion } from '../utils/motionPreferences';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Heading shown in the sheet header and used as the dialog's accessible name. */
  title: string;
  children: React.ReactNode;
}

export default function BottomSheet({ isOpen, onClose, title, children }: BottomSheetProps) {
  const reducedMotion = getInitialReducedMotion();

  // Escape to close + lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Bottom sheet — adaptive: caps at 80vh, inner content scrolls (Frank #4). */}
      <div
        className={`absolute inset-x-0 bottom-0 flex max-h-[80vh] flex-col rounded-t-2xl bg-oro-surface border-t border-oro-border shadow-card ${reducedMotion ? '' : 'oro-sheet-up'}`}
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-2 pb-1" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-oro-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-oro-border">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-11 h-11 -mr-2 rounded-lg hover:bg-oro-hover transition-colors"
            aria-label={`Close ${title.toLowerCase()}`}
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Scrollable content region — callers supply the sections. */}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
